/**
 * Permission System for StatDog Application - Frontend
 *
 * Permission Levels:
 * - NONE: Cannot view
 * - VIEW: Can only view
 * - EDIT: Can edit only (cannot add or delete)
 * - AED: Can Add, Edit, and Delete
 */

export enum PermissionLevel {
  NONE = 'none',
  VIEW = 'view',
  EDIT = 'edit',
  AED = 'aed',
}

export interface GameMenuPermissions {
  stats: PermissionLevel;
  statsLockAfterTimeout: PermissionLevel;
  details: PermissionLevel;
  kpis: PermissionLevel;
}

export interface GamesPermissions {
  uploadSchedule: PermissionLevel;
  game: PermissionLevel;
  practiceGames: boolean;
  gameMenu: GameMenuPermissions;
}

export interface UserPermissions {
  games: GamesPermissions;
  seasons: PermissionLevel;
  clients: PermissionLevel;
  users: PermissionLevel;
  players: PermissionLevel;
  opponents: PermissionLevel;
  dataSharing: boolean;
}

/**
 * Default permissions for each role
 *
 * NOTE: These are fallback defaults for offline/initial render only.
 * The actual source of truth is the backend API.
 * Always fetch from API_URLS.GET_DEFAULT_PERMISSIONS when possible.
 */
export const DEFAULT_PERMISSIONS: Record<string, UserPermissions> = {
  headCoach: {
    games: {
      uploadSchedule: PermissionLevel.AED,
      game: PermissionLevel.AED,
      practiceGames: true,
      gameMenu: {
        stats: PermissionLevel.AED,
        statsLockAfterTimeout: PermissionLevel.EDIT,
        details: PermissionLevel.EDIT,
        kpis: PermissionLevel.EDIT,
      },
    },
    seasons: PermissionLevel.AED,
    clients: PermissionLevel.NONE,
    users: PermissionLevel.AED,
    players: PermissionLevel.AED,
    opponents: PermissionLevel.AED,
    dataSharing: true,
  },

  assistantCoach: {
    games: {
      uploadSchedule: PermissionLevel.AED,
      game: PermissionLevel.AED,
      practiceGames: true,
      gameMenu: {
        stats: PermissionLevel.AED,
        statsLockAfterTimeout: PermissionLevel.NONE,
        details: PermissionLevel.EDIT,
        kpis: PermissionLevel.EDIT,
      },
    },
    seasons: PermissionLevel.AED,
    clients: PermissionLevel.NONE,
    users: PermissionLevel.AED,
    players: PermissionLevel.AED,
    opponents: PermissionLevel.AED,
    dataSharing: true,
  },

  statManager: {
    games: {
      uploadSchedule: PermissionLevel.AED,
      game: PermissionLevel.AED,
      practiceGames: false,
      gameMenu: {
        stats: PermissionLevel.AED,
        statsLockAfterTimeout: PermissionLevel.NONE,
        details: PermissionLevel.EDIT,
        kpis: PermissionLevel.NONE,
      },
    },
    seasons: PermissionLevel.AED,
    clients: PermissionLevel.NONE,
    users: PermissionLevel.AED,
    players: PermissionLevel.AED,
    opponents: PermissionLevel.AED,
    dataSharing: true,
  },

  // Note: This is a fallback definition (backend is the source of truth)
  statTaker: {
    games: {
      uploadSchedule: PermissionLevel.NONE,
      game: PermissionLevel.AED,
      practiceGames: false,
      gameMenu: {
        stats: PermissionLevel.AED,
        statsLockAfterTimeout: PermissionLevel.NONE,
        details: PermissionLevel.EDIT,
        kpis: PermissionLevel.NONE,
      },
    },
    seasons: PermissionLevel.NONE,
    clients: PermissionLevel.NONE,
    users: PermissionLevel.NONE,
    players: PermissionLevel.NONE,
    opponents: PermissionLevel.AED,
    dataSharing: false,
  },
};

/**
 * Get default permissions for a role
 */
export const getDefaultPermissions = (roleValue: string): UserPermissions => {
  return DEFAULT_PERMISSIONS[roleValue] || DEFAULT_PERMISSIONS.statTaker;
};

/**
 * Fetch default permissions from backend API
 * This is the source of truth - use this instead of the hardcoded defaults above
 */
export const fetchDefaultPermissionsFromBackend = async (): Promise<Record<string, UserPermissions> | null> => {
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    const response = await fetch(`${API_URL}/auth/user/default-permissions`);

    if (!response.ok) {
      console.error('Failed to fetch default permissions from backend');
      return null;
    }

    const data = await response.json();

    if (data.success && data.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching default permissions:', error);
    return null;
  }
};

/**
 * Permission hierarchy for comparison
 */
const PERMISSION_HIERARCHY: Record<PermissionLevel, number> = {
  [PermissionLevel.NONE]: 0,
  [PermissionLevel.VIEW]: 1,
  [PermissionLevel.EDIT]: 2,
  [PermissionLevel.AED]: 3,
};

/**
 * Check if user has permission for a specific resource
 */
export const hasPermission = (
  userPermissions: UserPermissions | null,
  resource: string,
  requiredLevel: PermissionLevel = PermissionLevel.VIEW,
): boolean => {
  if (!userPermissions) return false;

  // Navigate through nested permissions
  const resourcePath = resource.split('.');
  let currentPermission: any = userPermissions;

  for (const key of resourcePath) {
    if (!currentPermission || typeof currentPermission !== 'object') {
      return false;
    }
    currentPermission = currentPermission[key];
  }

  // If permission is boolean (for dataSharing)
  if (typeof currentPermission === 'boolean') {
    return currentPermission;
  }

  // If permission is a nested object, check if it's not NONE
  if (typeof currentPermission === 'object') {
    return true; // Has access to the section
  }

  // Check permission level
  if (!currentPermission || currentPermission === PermissionLevel.NONE) {
    return false;
  }

  // Type guard to ensure currentPermission is a PermissionLevel
  if (typeof currentPermission === 'string' && currentPermission in PERMISSION_HIERARCHY) {
    return PERMISSION_HIERARCHY[currentPermission as PermissionLevel] >= PERMISSION_HIERARCHY[requiredLevel];
  }

  return false;
};

/**
 * Check if user can perform specific action
 */
export const canPerformAction = (
  userPermissions: UserPermissions | null,
  resource: string,
  action: 'view' | 'add' | 'edit' | 'delete',
): boolean => {
  if (!userPermissions) return false;

  // Navigate through nested permissions
  const resourcePath = resource.split('.');
  let currentPermission: any = userPermissions;

  for (const key of resourcePath) {
    if (!currentPermission || typeof currentPermission !== 'object') {
      return false;
    }
    currentPermission = currentPermission[key];
  }

  if (!currentPermission || currentPermission === PermissionLevel.NONE) {
    return false;
  }

  // Ensure currentPermission is a string (PermissionLevel)
  if (typeof currentPermission !== 'string') {
    return false;
  }

  // Map actions to required permission levels
  switch (action.toLowerCase()) {
    case 'view':
      return currentPermission !== PermissionLevel.NONE;
    case 'edit':
      return currentPermission === PermissionLevel.EDIT || currentPermission === PermissionLevel.AED;
    case 'add':
    case 'delete':
      return currentPermission === PermissionLevel.AED;
    default:
      return false;
  }
};

/**
 * Merge custom permissions with default permissions
 */
export const mergePermissions = (
  defaultPerms: UserPermissions,
  customPerms: Partial<UserPermissions> | null,
): UserPermissions => {
  if (!customPerms) return defaultPerms;

  const merged = { ...defaultPerms };

  for (const key in customPerms) {
    const typedKey = key as keyof UserPermissions;
    const customValue = customPerms[typedKey];

    if (typeof customValue === 'object' && !Array.isArray(customValue) && customValue !== null) {
      (merged as any)[typedKey] = mergePermissions((merged as any)[typedKey] || {}, customValue as any);
    } else {
      (merged as any)[typedKey] = customValue;
    }
  }

  return merged;
};

/**
 * Role hierarchy for client roles (higher number = higher authority)
 * Used to determine if a user can manage another user
 */
export const ROLE_HIERARCHY: Record<string, number> = {
  headCoach: 4,
  assistantCoach: 3,
  statManager: 2,
  statTaker: 1,
};

/**
 * Check if current user's role is higher than or equal to target user's role
 * @param currentRoleValue - Current user's role value (e.g., 'headCoach')
 * @param targetRoleValue - Target user's role value
 * @returns true if current user can manage target user
 */
export const canManageUserRole = (currentRoleValue: string, targetRoleValue: string): boolean => {
  const currentLevel = ROLE_HIERARCHY[currentRoleValue] || 0;
  const targetLevel = ROLE_HIERARCHY[targetRoleValue] || 0;
  return currentLevel >= targetLevel;
};

/**
 * Get available roles that current user can assign
 * @param currentRoleValue - Current user's role value
 * @returns Array of role values that can be assigned
 */
export const getAssignableRoles = (currentRoleValue: string): string[] => {
  const currentLevel = ROLE_HIERARCHY[currentRoleValue] || 0;
  return Object.keys(ROLE_HIERARCHY).filter((roleValue) => ROLE_HIERARCHY[roleValue] <= currentLevel);
};

/**
 * Role display information
 */
export const ROLE_INFO = {
  headCoach: {
    label: 'Head Coach',
    description:
      'Primary administrator, responsible for account setup, user management, and overall team data – editing and sharing.',
    color: '#10B981', // green
  },
  assistantCoach: {
    label: 'Assistant Coach',
    description:
      'Assists with team management, with permissions that may be elevated by the head coach to be similar to a Head Coach based on configuration.',
    color: '#3B82F6', // blue
  },
  statManager: {
    label: 'Stat Manager',
    description:
      'Can be a Stat Taker, with administrative capabilities for managing team data (e.g., players, opponents, games) based on assigned permissions.',
    color: '#8B5CF6', // purple
  },
  statTaker: {
    label: 'Stat Taker',
    description:
      'Focuses solely on real-time stat capture during games. All users have access to Game Team and Players Reports.',
    color: '#F59E0B', // amber
  },
};

/**
 * Get permission level label for display
 */
export const getPermissionLevelLabel = (level: PermissionLevel): string => {
  switch (level) {
    case PermissionLevel.NONE:
      return 'None';
    case PermissionLevel.VIEW:
      return 'View';
    case PermissionLevel.EDIT:
      return 'Edit';
    case PermissionLevel.AED:
      return 'Add/Edit/Delete';
    default:
      return 'Unknown';
  }
};

/**
 * Get permission level description for display
 */
export const getPermissionLevelDescription = (level: PermissionLevel): string => {
  switch (level) {
    case PermissionLevel.NONE:
      return 'Cannot view or access';
    case PermissionLevel.VIEW:
      return 'Can view but not modify';
    case PermissionLevel.EDIT:
      return 'Can edit existing items only';
    case PermissionLevel.AED:
      return 'Full access - can add, edit, and delete';
    default:
      return '';
  }
};
